import ICONS from "../../../assets/Icons";
import globalText from "../../../utils/globalText";
import { globalTextFormat } from "../../../utils/globalTextFormat";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import FileFormatMapper from "../FileFormatMapper";

/**
 * @typedef FileOnClickParamType
 * @property {string} name
 * @property {"image" | "file"| "image-url"} type
 *
 * @callback FileOnClickType
 * @param {FileOnClickParamType} p
 * @returns {void}
 */
/**
 * @typedef {Object} AdditionalDetailsPreviewMapperProps
 * @property {Array<Object>} mappingData - List of additional fields with name, value, and data_type
 * @property {Object<string, string>} previewImages - Preloaded image URLs for uploaded files (keyed by field name & type)
 * @property { FileOnClickType} fileOnClick
 */

/**
 * Renders a read-only preview of all dynamic additional details.
 * Automatically handles all field types including upload, hyperlink,
 * date range, toggle, multi-select, month-year, and year ranges.
 *
 * @param {AdditionalDetailsPreviewMapperProps} props
 */

const AdditionalDetailsPreviewMapper = ({
  mappingData,
  previewImages,
  type,
  fileOnClick,
}) => {
  const safeMapping = Array.isArray(mappingData) ? mappingData : [];
  const safePreviewImages = previewImages || {};

  const convertToMonthYear = (input) => {
    if (!input) return globalText.NOT_GIVEN;

    const [month, year] = input.split(/[-,/ ]+/);

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const monthName = monthNames[parseInt(month, 10) - 1];

    return monthName && year ? `${monthName}, ${year}` : globalText.NOT_GIVEN;
  };

  const getFieldLabel = (field) =>
    `${field?.name || ""}${field?.mandatory === "Y" ? "" : " (optional)"}`;

  const hasFieldValue = (field) => {
    const { value, data_type } = field || {};

    if (!value || value === globalText.NOT_GIVEN) {
      return false;
    }

    if (data_type === "multi_select_list") {
      return Array.isArray(value) && value.length > 0;
    }

    return true;
  };

  const getFieldValue = (field) => {
    const { value, data_type } = field || {};

    switch (data_type) {
      case "upload":
        return hasFieldValue(field) ? value?.name : globalText.NOT_GIVEN;

      case "time":
      case "hyperlink":
        return value || globalText.NOT_GIVEN;

      case "from_year_to_year":
        return value?.from && value?.to
          ? `${value.from} - ${value.to}`
          : globalText.NOT_GIVEN;

      case "month_year":
        return convertToMonthYear(value);

      case "multi_select_list":
        return Array.isArray(value) && value.length
          ? value.join(", ")
          : globalText.NOT_GIVEN;

      case "date_range":
        return value?.start_date && value?.end_date
          ? `${value.start_date} - ${value.end_date}`
          : globalText.NOT_GIVEN;

      case "toggle":
        return value === true ? "Yes" : globalText.NOT_GIVEN;

      default:
        return value || globalText.NOT_GIVEN;
    }
  };

  const getFieldLabelText = (field) => {
    if (
      typeof field?.value === "object" &&
      field?.data_type === "from_year_to_year"
    ) {
      return `From year - To year${
        field?.mandatory === "Y" ? "" : " (optional)"
      }`;
    }
    return getFieldLabel(field);
  };

  const renderUploadValue = (field) => {
    const hasValue = hasFieldValue(field);
    if (!hasValue) {
      return (
        <div className="flex flex-col gap-gap-xs w-width-full">
          <p className="break-all">{globalText.NOT_GIVEN}</p>
          <p className="text-font-xs text-text-secondary">
            {getFieldLabel(field)}
          </p>
        </div>
      );
    }

    const fileExtension = field?.value?.name?.split(".")?.pop()?.toLowerCase();
    const fileType = field?.value?.type?.split("/")?.pop()?.toUpperCase();
    const previewImage =
      ["PNG", "BMP", "JPEG", "JPG"].includes(fileType) &&
      safePreviewImages[`${field?.name}_${field?.data_type}`];
    return (
      <div
        onClick={() => {
          if (previewImage)
            fileOnClick?.({
              name:
                field?.value?.file_url ?? `${field?.name}_${field?.data_type}`,
              type: (field?.value?.file_url ?? false) ? "image-url" : "image",
            });
          else {
            fileOnClick?.({
              name: field?.value,
              type: "file",
            });
          }
        }}
        className="flex justify-between items-start gap-gap-sm w-width-full"
      >
        <div className="flex-1 min-w-min-width-base">
          <p className="break-all">{linkIdentifier(field?.value?.name)}</p>
          <p className="text-font-xs text-text-secondary">
            {getFieldLabel(field)}
          </p>
        </div>
        <div className="flex items-center justify-end">
          <img
            src={
              field?.value?.file_url ??
              (previewImage || FileFormatMapper(fileExtension, "confirm"))
            }
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = ICONS?.DEFAULT_FILE;
            }}
            alt="file"
            className="w-icon-lg h-icon-lg shrink-0"
          />
        </div>
      </div>
    );
  };

  const renderFieldValue = (field) => {
    const { data_type, value } = field || {};
    if (data_type === "upload") {
      return renderUploadValue(field);
    }
    if (data_type === "hyperlink") {
      return value && value !== globalText.NOT_GIVEN
        ? linkIdentifier(value)
        : globalText.NOT_GIVEN;
    }
    return linkIdentifier(getFieldValue(field));
  };

  return (
    <div className="bg-primary-card rounded-border-radius-md p-padding-md w-width-full flex flex-col gap-gap-md">
      <span className="text-font-md font-semibold text-text-primary">
        {globalTextFormat(globalText.CATEGORY_SPECIFIC_DETAILS, [0, 1, 2])}
      </span>

      <div className="w-width-full flex flex-col">
        {safeMapping.length === 0 ? (
          <p className="text-left text-text-primary text-sm">
            {globalText.ADDITIONAL_DETAILS_UNSUPPORTED}
          </p>
        ) : (
          safeMapping.map((field, index) => (
            <div key={`${field?.name}_${field?.data_type}_${index}`}>
              <div className="w-width-full flex flex-col gap-gap-xs">
                <p
                  className={`text-font-md text-text-primary overflow-hidden whitespace-pre-line ${
                    field?.data_type === "hyperlink" ? "truncate" : "break-all"
                  }`}
                >
                  {renderFieldValue(field)}
                </p>

                {field?.data_type !== "upload" && (
                  <p className="text-font-xs text-text-secondary">
                    {getFieldLabelText(field)}
                  </p>
                )}
              </div>

              {index !== safeMapping.length - 1 && (
                <div className="border-b border-primary-bg my-margin-sm" />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdditionalDetailsPreviewMapper;
